import React, { useState, useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { styled } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';

const FullWidthTextField = styled(TextField)({
  width: '100%',
});
const RightGrid = styled(Grid)({
  textAlign: 'right',
});
const MarginButton = styled(Button)({
  marginTop: 8,
  marginBottom: 8,
});

const mapState = createSelector(
  ({ surveyParcels }) => surveyParcels,
  ({ invalidParcels }) => invalidParcels,
  (surveyParcels, invalidParcels) => ({
    surveyParcels,
    invalidParcels,
  })
);

export const SelectParcels = ({ survey }) => {
  const [value, setValue] = useState('');
  const { surveyParcels: surveyParcelsAll, invalidParcels } = useSelector(
    mapState
  );
  const dispatch = useDispatch();

  const surveyParcels = Object.values(surveyParcelsAll[survey.id] || {});

  useEffect(() => {
    dispatch({
      type: 'FETCH_SURVEY_PARCELS',
      payload: { survey },
    });
  }, [survey, dispatch]);

  const checkParcels = useCallback(() => {
    const parcelIds = value
      .split(',')
      .map((str) => str.trim())
      .filter(Boolean);

    setValue('');
    const parcelIdsSet = new Set([
      ...parcelIds,
      ...surveyParcels.map(({ parcel_id: parcelId }) => parcelId),
    ]);

    dispatch({
      type: 'CREATE_SURVEY_PARCELS',
      payload: {
        survey,
        parcelIds: Array.from(parcelIdsSet),
      },
    });
  }, [survey, value, setValue, dispatch, surveyParcels]);

  const deselectParcel = useCallback(
    (surveyParcel) => {
      dispatch({
        type: 'DELETE_SURVEY_PARCEL',
        payload: {
          survey,
          surveyParcel,
        },
      });
    },
    [dispatch, survey]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} container>
        <Typography variant="h6">
          Which parcels do you want to survey?
        </Typography>
        <br />
        <FullWidthTextField
          multiline
          value={value}
          rows={4}
          placeholder="Type or paste comma separated parcel ids"
          variant="outlined"
          onChange={(e) => setValue(e.target.value)}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>
          {surveyParcels.length}
          &nbsp;parcels selected
        </Typography>
      </Grid>
      <Grid item xs={4} />
      <RightGrid item xs={4}>
        <MarginButton
          onClick={checkParcels}
          color="primary"
          variant="contained"
        >
          Add Parcels
        </MarginButton>
      </RightGrid>
      {surveyParcels.map((surveyParcel) => (
        <Grid item key={surveyParcel.parcel_id}>
          <Chip
            label={surveyParcel.parcel_id}
            onDelete={() => deselectParcel(surveyParcel)}
          />
          &nbsp;
        </Grid>
      ))}

      <Dialog open={Boolean(invalidParcels.length)}>
        <DialogContent>
          <Typography variant="h6">
            The following parcel ids could not be found in our system:
          </Typography>
          <Typography variant="body1">{invalidParcels.join(', ')}</Typography>
          <DialogActions>
            <Button
              onClick={() => {
                dispatch({ type: 'CLEAR_INVALID_PARCELS' });
              }}
            >
              Continue
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};
